import React, { useEffect } from "react";

import { css, useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import shallow from "zustand/shallow";

import LoadingIndicator from "../../../../shared/LoadingIndicator";
import NewHeadline from "../../../../shared/NewHeadline";
import { ChoiceButton } from "../../../../shared/OrderChoiceButtons";
import useSnackbar from "../../../../shared/Snackbar/store";
import useCartStore from "../../ShoppingCart/store";
import useCheckoutStore, { PaymentMethods } from "./../store";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    row-gap: 20px;
`;

interface iStepPaymentMethodProps {
    children?: React.ReactNode;
}

const StepPaymentMethod = ({ children }: iStepPaymentMethodProps) => {
    const theme = useTheme();
    const { notify } = useSnackbar();

    const {
        paymentMethods,
        isLoading,
        selectedPaymentMethod,
        fetchPaymentMethods,
        updatePaymentMethod,
    } = useCheckoutStore(
        (state) => ({
            paymentMethods: state.paymentMethods,
            isLoading: state.isPaymentMethodsLoading,
            selectedPaymentMethod: state.selectedPaymentMethod,
            fetchPaymentMethods: state.fetchPaymentMethods,
            updatePaymentMethod: state.updatePaymentMethod,
        }),
        shallow
    );

    const { isTechnicCart } = useCartStore(
        (state) => ({
            isTechnicCart: state.isTechnicCart,
        }),
        shallow
    );

    useEffect(() => {
        if (paymentMethods.length <= 0) {
            if (isTechnicCart) {
                fetchPaymentMethods([
                    PaymentMethods.Vorkasse,
                    PaymentMethods.PayPal,
                ]);
            } else {
                fetchPaymentMethods([PaymentMethods.Rechnung]);
            }
        }
    }, [isTechnicCart]);

    const handleChange = async (event: React.SyntheticEvent<HTMLElement>) => {
        const selectedPaymentMethodId =
            event.currentTarget?.dataset?.paymentMethodId ?? undefined;
        const method = await updatePaymentMethod(selectedPaymentMethodId);

        if (method) {
            notify("Erfolgreich", `Zahlungsmethode ${method.name} ausgewählt.`);
            return;
        }
        notify(
            "Fehler",
            "Zahlungsmethode konnte nicht gespeichert werden, bitte versuche es zu einem späteren Zeitpunkt erneut."
        );
    };

    if (isLoading) {
        return <LoadingIndicator />;
    }

    const isActive = css`
        color: ${theme.colors.white.toString()};
        background-color: ${theme.colors.red.toString()};
        border-color: ${theme.colors.red.toString()};
    `;

    return (
        <>
            <NewHeadline>Zahlart</NewHeadline>
            <Container>
                {paymentMethods.map((method) => (
                    <ChoiceButton
                        className="choiceButton"
                        data-payment-method-id={method.id}
                        onClick={handleChange}
                        key={method.id}
                        css={css`
                            ${selectedPaymentMethod?.id === method.id
                                ? isActive
                                : ""}
                        `}
                    >
                        {method.name}
                    </ChoiceButton>
                ))}
            </Container>
            {children}
        </>
    );
};

export default StepPaymentMethod;
